import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";
import { panelAPI } from "../../services/api";
import { REP_ROLES } from "../../utils/constants";
import Dropdown from "../inputs/Dropdown";
import Modal from "../shared/Modal";

// Validation schema for inviting existing reps
const inviteRepSchema = z.object({
  repRole: z.string().min(1, "Rep role is required"),
});

const InviteRepModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  canAddRep,
  requirements,
}) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(inviteRepSchema),
    defaultValues: {
      repRole: "",
    },
  });

  const selectedRepRole = watch("repRole");

  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const params = {};
      if (selectedRepRole) {
        params.role = selectedRepRole;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await panelAPI.getUsersForInvite(params);
      if (response.success) {
        setUsers(response.users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }, [selectedRepRole, searchTerm]);

  // Fetch users based on search term and role
  useEffect(() => {
    if (isOpen && selectedRepRole) {
      fetchUsers();
    }
  }, [isOpen, selectedRepRole, searchTerm, fetchUsers]);

  const handleClose = () => {
    reset();
    setUsers([]);
    setSearchTerm("");
    setSelectedUsers([]);
    onClose();
  };

  // Ensure form is properly initialized
  const isFormValid = selectedRepRole && selectedUsers.length > 0;

  const handleUserToggle = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleFormSubmit = async (data) => {
    if (selectedUsers.length === 0) {
      toast.warning("Please select at least one user to invite");
      return;
    }

    if (!data || !data.repRole) {
      toast.error("Please select a rep role");
      return;
    }

    try {
      // Submit each selected user
      const promises = selectedUsers.map((userId) =>
        onSubmit({
          userId: userId.toString(),
          repRole: data.repRole,
        })
      );

      const results = await Promise.allSettled(promises);

      // Count successes
      const successCount = results.filter(
        (r) => r.status === "fulfilled"
      ).length;
      const failCount = results.filter((r) => r.status === "rejected").length;

      if (successCount > 0) {
        toast.success(`Successfully invited ${successCount} rep(s)!`);
      }

      if (failCount > 0) {
        toast.warning(`${failCount} invitation(s) failed (may be duplicates)`);
      }

      reset();
      setSelectedUsers([]);
      handleClose();
    } catch (error) {
      console.error("Error inviting reps:", error);
      toast.error("Failed to invite reps");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite Existing Users"
      size="lg"
      onConfirm={isFormValid ? handleSubmit(handleFormSubmit) : undefined}
      onCancel={handleClose}
      confirmText={
        loading ? "Inviting..." : `Invite ${selectedUsers.length} Rep(s)`
      }
      cancelText="Cancel"
      loading={loading}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <Controller
          name="repRole"
          control={control}
          render={({ field }) => (
            <Dropdown
              headLabel="Rep Role"
              label="Select rep role"
              options={REP_ROLES.filter((role) =>
                canAddRep ? canAddRep(role.value) : true
              )}
              value={field.value}
              onSelect={field.onChange}
              required
              error={errors.repRole}
            />
          )}
        />

        {requirements && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-blue-900 mb-2">
              Rep Requirements for {requirements.description}
            </h4>
            <div className="text-sm text-blue-800">
              <p>Community Members: {requirements.communityReps} required</p>
              <p>FEMA Members: {requirements.clientReps} required</p>
            </div>
          </div>
        )}

        {!selectedRepRole && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Please select a rep role first</strong> to search for
              users to invite.
            </p>
          </div>
        )}

        {selectedRepRole && (
          <>
            <div>
              <label
                htmlFor="invite-rep-search"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Search Users
              </label>
              <input
                id="invite-rep-search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <p className="block text-sm font-medium text-gray-700 mb-2">
                Select Users ({users.length} available)
              </p>
              <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                {loadingUsers ? (
                  <div className="p-4 text-center text-gray-500">
                    Loading users...
                  </div>
                ) : users.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No users found. Try adjusting your search.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {users.map((user) => (
                      <label
                        key={user.id}
                        className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors flex items-center ${
                          selectedUsers.includes(user.id) ? "bg-blue-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                          {user.role}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> Select one or more existing users from the
            database to add as reps to this panel.
          </p>
          {selectedUsers.length > 0 && (
            <p className="text-sm text-blue-800 mt-2">
              <strong>{selectedUsers.length} user(s) selected</strong> - Click
              "Invite" to proceed
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default InviteRepModal;
